// IA Bundle builder page — <ia-bundle-builder> (one pick per step, steps are
// <ia-accordion-component> blocks that collapse once picked; a fixed bottom
// bar mirrors the picks with one slot per step).
//
// Extends IaBundleBase (ia-bundle.js): add-to-cart posts ONLY the picked
// products in one call — every line tagged _bundle_id / _bundle_parent /
// _bundle_title (+ _bundle_step); the bundle app's cart-transform merges
// them into one line under the parent product (docs/ia-bundles.md).
//
// Slots are server-rendered <template> nodes inside each step block — this
// component only clones them into the bar and toggles state.
import { IaBundleBase } from 'ia-bundle'
// Steps are hand-authored <ia-accordion-component> markup — load its element.
import 'ia-accordion'

const PROGRESS_FALLBACK = "You're {count} products away from {discount}% off!"
const ACHIEVED_FALLBACK = "You've got {discount}% off!"
// {discount}% keeps the percent sign inside the accent span.
const MESSAGE_TOKENS = /(\{count\}|\{discount\}%?)/
const MOBILE_MQ = window.matchMedia('(max-width: 1023px)')
const REDUCED_MOTION_MQ = window.matchMedia('(prefers-reduced-motion: reduce)')
// Gap kept between the sticky header and the step scrolled into view.
const SCROLL_GAP = 16
// Grace after the fold's duration before the ride ends without transitionend.
const SCROLL_GRACE_MS = 200
// Shopper scrolling that cancels a ride instead of fighting it.
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '])

class IaBundleBuilder extends IaBundleBase {
  connectedCallback() {
    super.connectedCallback()
    this.steps = Array.from(this.querySelectorAll('[data-bundle-step]'))
    this.bar = this.querySelector('[data-bundle-bar]')
    // The bar ships collapsed for mobile; a desktop that opted into folding
    // (ia-bundle-bar--desktop-collapsible) starts open, the caret folds it.
    if (this.bar?.classList.contains('ia-bundle-bar--desktop-collapsible') && !MOBILE_MQ.matches) {
      this.setBarExpanded(true)
    }
    this.slotsEl = this.querySelector('[data-bundle-slots]')
    this.messageEl = this.querySelector('[data-bundle-message]')
    this.selections = new Map() // step element -> picked card element

    this.buildSlots()
    this.addEventListener('click', this.handleClick.bind(this))
    this.onBlockSelect = this.handleBlockSelect.bind(this)
    document.addEventListener('shopify:block:select', this.onBlockSelect)
    this.observeBarHeight()
    this.updateState()
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:block:select', this.onBlockSelect)
    this.barObserver?.disconnect()
    this.scrollController?.abort()
    document.documentElement.style.removeProperty('--ia-bundle-bar-height')
  }

  // Each step ships its sticky-bar slot as an inert <template>; clone them
  // into the bar in step order (cloning keeps re-init in the editor safe).
  buildSlots() {
    this.slots = this.steps.map((step) => {
      const slot = step
        .querySelector('template[data-bundle-slot-template]')
        ?.content.firstElementChild?.cloneNode(true)
      if (slot) this.slotsEl?.append(slot)
      return slot
    })
  }

  // Content flows under the fixed bar — publish its height so the section
  // can pad itself clear, and on the root so the chat launcher rides above it.
  observeBarHeight() {
    if (!this.bar || typeof ResizeObserver === 'undefined') return
    this.barObserver = new ResizeObserver(() => {
      const height = `${this.bar.offsetHeight}px`
      this.style.setProperty('--ia-bb-bar-height', height)
      document.documentElement.style.setProperty('--ia-bundle-bar-height', height)
    })
    this.barObserver.observe(this.bar)
  }

  handleClick(event) {
    const card = event.target.closest('[data-bundle-card]')
    if (card) return this.toggleCard(card)

    const remove = event.target.closest('[data-slot-remove]')
    if (remove) return this.removeStep(this.steps[this.slots.indexOf(remove.closest('[data-bundle-slot]'))])

    if (event.target.closest('[data-bar-toggle]')) this.toggleBar()
  }

  // Theme editor: selecting a step block (or anything inside it) must reveal
  // it even when the builder collapsed it.
  handleBlockSelect(event) {
    const step = this.steps.find((candidate) => candidate.contains(event.target))
    step?.open?.()
  }

  toggleCard(card) {
    const step = card.closest('[data-bundle-step]')
    if (!step) return
    if (this.selections.get(step) === card) return this.removeStep(step)
    this.selections.set(step, card)
    this.renderStep(step)
    step.close?.()
    this.updateState()
    this.autoExpandBar()
    this.openNextIncomplete(step)
  }

  removeStep(step) {
    if (!step || !this.selections.has(step)) return
    this.selections.delete(step)
    this.renderStep(step)
    step.open?.()
    this.updateState()
  }

  // Reflect a step's selection on its cards and its bar slot.
  renderStep(step) {
    const picked = this.selections.get(step)
    step.classList.toggle('is-complete', Boolean(picked))
    step.querySelectorAll('[data-bundle-card]').forEach((card) => {
      const selected = card === picked
      card.classList.toggle('is-selected', selected)
      card.setAttribute('aria-pressed', String(selected))
    })

    const slot = this.slots[this.steps.indexOf(step)]
    if (!slot) return
    const media = slot.querySelector('[data-slot-media]')
    const removeButton = slot.querySelector('[data-slot-remove]')
    slot.classList.toggle('has-product', Boolean(picked))
    if (removeButton) removeButton.hidden = !picked
    if (media) {
      media.textContent = ''
      const image = picked?.querySelector('[data-card-image] :is(img, svg)')
      if (image) media.append(image.cloneNode(true))
    }
  }

  openNextIncomplete(fromStep) {
    const start = this.steps.indexOf(fromStep) + 1
    const order = this.steps.slice(start).concat(this.steps.slice(0, start))
    const next = order.find((step) => !this.selections.has(step))
    if (next) {
      next.open?.()
      return this.scrollToStep(next, fromStep)
    }
    // Every step picked: nothing reopens, and the page returns to the first
    // step on every viewport, or the shopper is left facing the closed last
    // step and the blank below it.
    this.scrollToStep(this.steps[0], fromStep)
  }

  scrollOffset() {
    const styles = getComputedStyle(document.documentElement)
    return (parseFloat(styles.getPropertyValue('--ia-header-height')) || 0) + SCROLL_GAP
  }

  // Ride the fold: the picked step's grid is bottom-anchored (CSS), so its
  // collapse already moves everything up like a scroll. Each frame the step's
  // on-screen top is placed along y0 → offset at the fold's MEASURED progress,
  // so cells, header and viewport move as one sheet with the fold's own easing.
  // transitionend (or a grace timeout) snaps the exact end; shopper scrolling
  // cancels the ride.
  scrollToStep(step, closingStep) {
    this.scrollController?.abort()
    const controller = new AbortController()
    const { signal } = controller
    this.scrollController = controller
    const cancel = () => controller.abort()
    window.addEventListener('wheel', cancel, { signal, passive: true })
    window.addEventListener('touchstart', cancel, { signal, passive: true })
    window.addEventListener('keydown', (event) => SCROLL_KEYS.has(event.key) && cancel(), { signal })

    const pin = (top) => window.scrollBy(0, Math.round(step.getBoundingClientRect().top - top))
    const finish = () => {
      if (signal.aborted) return
      controller.abort()
      if (step.isConnected) pin(this.scrollOffset())
    }

    const body = closingStep?.querySelector('.ia-accordion__body')
    const startHeight = body?.getBoundingClientRect().height || 0
    const duration = body ? parseFloat(getComputedStyle(body).transitionDuration) * 1000 || 0 : 0
    if (!startHeight || !duration) return finish()

    body.addEventListener('transitionend', (event) => {
      if (event.propertyName === 'grid-template-rows') finish()
    }, { signal })
    setTimeout(finish, duration + SCROLL_GRACE_MS)
    if (REDUCED_MOTION_MQ.matches) return

    const from = step.getBoundingClientRect().top
    const tick = () => {
      if (signal.aborted) return
      if (!step.isConnected) return cancel()
      const progress = Math.min(Math.max(1 - body.getBoundingClientRect().height / startHeight, 0), 1)
      pin(from + (this.scrollOffset() - from) * progress)
      requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  }

  toggleBar() {
    this.setBarExpanded(this.bar?.classList.contains('is-collapsed'))
  }

  setBarExpanded(expanded) {
    if (!this.bar) return
    this.bar.classList.toggle('is-collapsed', !expanded)
    this.bar.querySelector('[data-bar-toggle]')?.setAttribute('aria-expanded', String(expanded))
  }

  // Mobile strategy (data-bar-auto-expand = pick count, 0 = never): the bar
  // opens itself once that many products are picked. Opening an open bar is
  // a no-op, so a desktop that folds (ia-bundle-bar--desktop-collapsible)
  // only ever gains from this.
  autoExpandBar() {
    const threshold = parseInt(this.dataset.barAutoExpand) || 0
    if (threshold && this.selections.size >= threshold) this.setBarExpanded(true)
  }

  requiredSteps() {
    return this.steps.filter((step) => !step.hasAttribute('data-step-optional'))
  }

  updateState() {
    let total = 0
    let compare = 0
    this.selections.forEach((card) => {
      const price = parseInt(card.dataset.price) || 0
      total += price
      compare += Math.max(parseInt(card.dataset.compare) || 0, price)
    })

    const remaining = this.requiredSteps().filter((step) => !this.selections.has(step)).length
    const complete = remaining === 0 && this.steps.length > 0

    this.renderTotal(total, compare)
    this.renderMessage(complete, remaining)
    if (this.atcButton) this.atcButton.hidden = !complete
    this.setCanSubmit(complete)
    this.bar?.classList.toggle('is-complete', complete)
  }

  // "You're {count} products away from {discount}% off!" — token values get
  // the pink accent span. Built with DOM nodes, never innerHTML.
  renderMessage(complete, remaining) {
    if (!this.messageEl) return
    const format = complete
      ? this.dataset.achievedText || ACHIEVED_FALLBACK
      : this.dataset.progressText || PROGRESS_FALLBACK

    this.messageEl.textContent = ''
    format.split(MESSAGE_TOKENS).forEach((part) => {
      if (!part) return
      if (part.startsWith('{count}') || part.startsWith('{discount}')) {
        const accent = document.createElement('span')
        accent.className = 'ia-bundle-bar__accent'
        accent.textContent = part.startsWith('{count}')
          ? String(remaining)
          : part.replace('{discount}', String(this.discountPercent))
        this.messageEl.append(accent)
      } else {
        this.messageEl.append(part)
      }
    })
  }

  collectChildren() {
    const children = []
    this.steps.forEach((step) => {
      const card = this.selections.get(step)
      if (!card) return
      children.push({
        id: Number(card.dataset.variantId),
        quantity: 1,
        step: step.dataset.stepTitle || undefined
      })
    })
    return children.length ? children : null
  }

  // Fresh builder after a successful add: clear picks, reopen every step.
  reset() {
    this.selections.clear()
    this.steps.forEach((step) => {
      this.renderStep(step)
      step.open?.()
    })
    this.setBarExpanded(false)
    this.updateState()
  }
}

if (!customElements.get('ia-bundle-builder')) {
  customElements.define('ia-bundle-builder', IaBundleBuilder)
}
